/**
 * lib/AuthContext.tsx
 * --------------------
 * What: React context/provider wrapping Supabase auth, exposing `user`, `session`,
 * loading state, and basic `signUp`/`signIn`/`signOut` helpers.
 * Why: Centralises auth lifecycle and side-effects (e.g., logging login events)
 * so screens/components stay declarative.
 * Notes: Persists session via the global Supabase client; emits verbose logs in
 * development to aid debugging.
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from './supabase';
import { Session, User } from '@supabase/supabase-js';
import { useNavigationContext } from '../app/navigation/NavigationContext';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { getPlatform } from '../app/utils/platform';
import CustomAlert from '../app/components/CustomAlert';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: { user: User | null; session: Session | null }; error: any }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function logLoginEvent(userId: string, method: string) {
  try {
    // Build a simple user-agent string
    let userAgent = '';
    if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
      userAgent = navigator.userAgent;
    } else {
      userAgent = `${Platform.OS}; appVersion=${Constants.expoConfig?.version || Constants.appOwnership || 'unknown'}`;
    }

    let ipAddress: string | null = null;
    try {
      const resp = await fetch('https://api.ipify.org?format=json');
      const json = await resp.json();
      ipAddress = json.ip;
    } catch (err) {
      console.warn('[AuthProvider] Unable to fetch IP for login event:', err);
    }

    const row: any = {
      user_id: userId,
      login_method: method,
      user_agent: userAgent,
    };
    if (ipAddress) row.ip_address = ipAddress;

    await supabase.from('login_events').insert(row);
  } catch (err) {
    console.error('[AuthProvider] Failed to log login event:', err);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { resetNavigation } = useNavigationContext();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTitle, setAuthModalTitle] = useState('');
  const [authModalMessage, setAuthModalMessage] = useState('');

  console.log('🔐 [AuthProvider] Provider rendered with state:', {
    hasUser: !!user,
    hasSession: !!session,
    loading,
    userId: user?.id
  });

  useEffect(() => {
    console.log('🔐 [AuthProvider] Setting up auth listeners');
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔐 [AuthProvider] Initial session check:', { hasSession: !!session, userId: session?.user?.id });
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('🔐 [AuthProvider] Auth state changed:', { event: _event, hasSession: !!session, userId: session?.user?.id });
      setSession(session);
      setUser(session?.user ?? null);
    });

    return () => {
      console.log('🔐 [AuthProvider] Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  // Handle Supabase email confirmation callback on web and show a modal
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    const params = url.searchParams;
    const hash = window.location.hash || '';
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

    const type = params.get('type') || hashParams.get('type');
    const code = params.get('code');
    const tokenHash = params.get('token_hash');
    const accessToken = hashParams.get('access_token');

    const isEmailChange = type === 'email_change' || params.get('auth') === 'email_change';
    const needsSessionExchange =
      // Only exchange for flows that establish a session (magic link / recovery / oauth)
      (type === 'recovery' || type === 'magiclink' || type === 'signup' || type === 'invite' || !!code || !!accessToken);

    if (!isEmailChange && !needsSessionExchange) return;

    (async () => {
      try {
        // For non-email-change flows, exchange the code/token in the URL for a session
        if (needsSessionExchange) {
          const { error } = await supabase.auth.exchangeCodeForSession(window.location.href as any);
          if (error) throw error;
        }

        if (isEmailChange) {
          setAuthModalTitle('Email Updated');
          setAuthModalMessage('Your email address has been confirmed and updated.');
          setShowAuthModal(true);
        }
      } catch (err: any) {
        setAuthModalTitle('Link Error');
        setAuthModalMessage(err?.message || 'The confirmation link is invalid or has expired.');
        setShowAuthModal(true);
      } finally {
        // Clean only auth-related params so the modal does not reappear on refresh
        try {
          const cleaned = new URL(window.location.href);
          const sp = cleaned.searchParams;
          const hp = new URLSearchParams(cleaned.hash.startsWith('#') ? cleaned.hash.slice(1) : cleaned.hash);
          // Remove known auth params
          ['type', 'code', 'token_hash', 'auth', 'redirect_to'].forEach((k) => sp.delete(k));
          ['access_token', 'refresh_token', 'expires_in', 'expires_at', 'token_type', 'provider_token', 'type'].forEach((k) => hp.delete(k));
          const newHash = hp.toString();
          const newUrl =
            cleaned.origin +
            cleaned.pathname +
            (sp.toString() ? `?${sp.toString()}` : '') +
            (newHash ? `#${newHash}` : '');
          window.history.replaceState({}, document.title, newUrl);
        } catch {}
      }
    })();
  }, []);

  const value = {
    user,
    session,
    loading,
    signUp: async (email: string, password: string) => {
      console.log('🔐 [AuthProvider] signUp called for email:', email);
      // Capture sign-up IP (non-blocking; fallback to null)
      let signupIp: string | null = null;
      try {
        const resp = await fetch('https://api.ipify.org?format=json');
        const json = await resp.json();
        signupIp = json.ip;
      } catch {}

      const response = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            signup_ip: signupIp,
            created_with: getPlatform(),
          },
        },
      });
      console.log('🔐 [AuthProvider] signUp response:', { hasError: !!response.error, hasUser: !!response.data.user });
      // Only log the sign-up event if Supabase returned a session
      // (i.e., when email confirmation is disabled). When email confirmation
      // is required, no session is returned and logging would fail RLS (401).
      if (!response.error && response.data.session && response.data.user) {
        await logLoginEvent(response.data.user.id, 'email_sign_up');
      }
      return response;
    },
    signIn: async (email: string, password: string) => {
      console.log('🔐 [AuthProvider] signIn called for email:', email);
      const response = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log('🔐 [AuthProvider] signIn response:', { hasError: !!response.error, hasUser: !!response.data.user });
      if (!response.error && response.data.user) {
        await logLoginEvent(response.data.user.id, 'email_password');
      }
      return response;
    },
    signOut: async () => {
      console.log('🔐 [AuthProvider] signOut called');
      await supabase.auth.signOut({ scope: 'local' });
      resetNavigation();
    },
  };

  console.log('📤 [AuthProvider] Returning context with user:', !!user);

  return (
    <>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
      <CustomAlert
        visible={showAuthModal}
        title={authModalTitle}
        message={authModalMessage}
        onDismiss={() => setShowAuthModal(false)}
      />
    </>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  console.log('🔐 [useAuth] Hook called, returning context with user:', !!context.user);
  return context;
} 